// Cookie consent state + the only place the TikTok Pixel is ever loaded or torn down.
import api from "@/lib/api";

export const CONSENT_VERSION = "2026-11-02";
const KEY = "hb_consent";
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const PIXEL_ID = "DBIO7UBC77UFSQ3BTUKG";
const PIXEL_SRC = "https://analytics.tiktok.com/i18n/pixel/events.js";
const TIKTOK_COOKIES = ["_ttp", "_tt_enable_cookie", "_tt_sessionId", "tt_sessionid", "tt_appInfo", "tt_pixel_session_index"];

export const getConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(KEY));
    if (!c || c.version !== CONSENT_VERSION) return null;
    if (Date.now() - Date.parse(c.decided_at) > MAX_AGE_MS) return null;
    return c;
  } catch { return null; }
};

export const hasAdConsent = () => getConsent()?.advertising === true;

const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const saveConsent = ({ advertising = false, analytics = false, method = "custom" }) => {
  const prev = getConsent();
  const record = {
    version: CONSENT_VERSION,
    consent_id: prev?.consent_id || uuid(),
    essential: true,
    advertising: !!advertising,
    analytics: !!analytics,
    method,
    decided_at: new Date().toISOString(),
  };
  localStorage.setItem(KEY, JSON.stringify(record));
  if (record.advertising) { loadTikTok(); window.ttq?.page?.(); } else unloadTikTok();
  if (!record.analytics) clearLegacyAnalytics();
  window.dispatchEvent(new CustomEvent("hb-consent", { detail: record }));
  api.post("/consent", record).catch(() => {}); // anonymous evidence log — never blocks the shopper
  return record;
};

export const openCookieSettings = () => window.dispatchEvent(new Event("hb-cookie-settings"));

// --- TikTok Pixel lifecycle -------------------------------------------------

export const loadTikTok = () => {
  if (!hasAdConsent() || typeof window === "undefined") return;
  if (document.getElementById("tiktok-pixel")) return;
  const w = window;
  const t = "ttq";
  w.TiktokAnalyticsObject = t;
  const ttq = (w[t] = w[t] || []);
  ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group",
    "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
  ttq.setAndDefer = (obj, m) => { obj[m] = function () { obj.push([m].concat(Array.prototype.slice.call(arguments, 0))); }; };
  ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
  ttq.instance = (id) => { const e = ttq._i[id] || []; ttq.methods.forEach((m) => ttq.setAndDefer(e, m)); return e; };
  ttq.load = (id, opts) => {
    ttq._i = ttq._i || {}; ttq._i[id] = []; ttq._i[id]._u = PIXEL_SRC;
    ttq._t = ttq._t || {}; ttq._t[id] = +new Date();
    ttq._o = ttq._o || {}; ttq._o[id] = opts || {};
    const s = document.createElement("script");
    s.id = "tiktok-pixel"; s.type = "text/javascript"; s.async = true;
    s.src = `${PIXEL_SRC}?sdkid=${id}&lib=${t}`;
    document.head.appendChild(s);
  };
  ttq.load(PIXEL_ID);
  // PageView is fired by the router effect (App.js) or by saveConsent — not here, to avoid double counting
};

const expireCookie = (name) => {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [undefined, host, `.${host}`];
  for (let i = 1; i < parts.length - 1; i++) domains.push(`.${parts.slice(i).join(".")}`);
  domains.forEach((d) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
  });
};

export const unloadTikTok = () => {
  try { window.ttq?.revokeConsent?.(); window.ttq?.disableCookie?.(); } catch { /* best effort */ }
  document.getElementById("tiktok-pixel")?.remove();
  document.querySelectorAll('script[src*="analytics.tiktok.com"]').forEach((s) => s.remove());
  try { delete window.ttq; delete window.TiktokAnalyticsObject; } catch { window.ttq = undefined; }
  TIKTOK_COOKIES.forEach(expireCookie);
  try { Object.keys(sessionStorage).filter((k) => k.startsWith("ttq_")).forEach((k) => sessionStorage.removeItem(k)); } catch { /* private mode */ }
};

// Names visible to document.cookie right now — used to show the shopper what was (or wasn't) cleared.
export const visibleTikTokCookies = () =>
  document.cookie.split(";").map((c) => c.trim().split("=")[0]).filter((n) => TIKTOK_COOKIES.includes(n));

// A PostHog snippet shipped with the original site scaffold until 2 Nov 2026; sweep anything it left behind.
export const clearLegacyAnalytics = () => {
  document.cookie.split(";").map((c) => c.trim().split("=")[0]).filter((n) => n.startsWith("ph_")).forEach(expireCookie);
  try { Object.keys(localStorage).filter((k) => k.startsWith("ph_") || k.startsWith("__posthog")).forEach((k) => localStorage.removeItem(k)); } catch { /* private mode */ }
};
